import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import {
    Plus,
    Edit01,
    SearchLg,
    ArrowRight,
    FileCheck02
} from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Table, TableCard } from '@/components/application/table/table';
import { Modal, DialogTrigger, Dialog, ModalOverlay } from '@/components/application/modals/modal';
import { documentSchemaService, documentAttributeService, DOCUMENT_TYPES } from '@/data/mock-document-schemas';
import { useTranslation } from 'react-i18next';
import type { DocumentType } from '@/types/document-schema';

interface DocumentTypeWithStats {
    key: DocumentType;
    label: string;
    description: string;
    schemasCount: number;
    attributesCount: number;
}

export const DocumentTypesPage = () => {
    const { t } = useTranslation(['pages:documents-admin', 'common']);
    const [documentTypesWithStats, setDocumentTypesWithStats] = useState<DocumentTypeWithStats[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [attributeFilter, setAttributeFilter] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingDocType, setEditingDocType] = useState<DocumentTypeWithStats | null>(null);

    const [formData, setFormData] = useState({
        key: '' as DocumentType | '',
        label: '',
        description: '',
    });

    // Get all attributes for filter
    const allAttributes = documentAttributeService.getAll();
    const attributeOptions = [
        { id: '', label: t('types.filters.allAttributes') },
        ...allAttributes.map((attr) => ({ id: attr.document_attribute_id, label: `${attr.label} (${attr.attribute_key})` })),
    ];

    useEffect(() => {
        loadDocumentTypes();
    }, [searchTerm, attributeFilter]);

    const loadDocumentTypes = () => {
        setIsLoading(true);

        let filtered = DOCUMENT_TYPES.map((dt) => ({
            key: dt.key,
            label: dt.label,
            description: dt.description,
            schemasCount: documentSchemaService.getSchemasCountByDocumentType(dt.key),
            attributesCount: documentSchemaService.getAttributesCountByDocumentType(dt.key),
        }));

        // Filter by keyword
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(
                (dt) =>
                    dt.key.toLowerCase().includes(term) ||
                    dt.label.toLowerCase().includes(term) ||
                    dt.description.toLowerCase().includes(term)
            );
        }

        // Filter by specific attribute
        if (attributeFilter) {
            const attribute = documentAttributeService.getById(attributeFilter);
            if (attribute) {
                // Get all schemas that use this attribute
                const schemasWithAttribute = documentSchemaService.getAll().filter(() => {
                    // Check if any schema attribute uses this attribute
                    // In real implementation, we'd query schema attributes
                    // For now, we'll check if the attribute is used in any schema of this document type
                    // Simplified: if attribute is global or has high usage, consider it used
                    return attribute.is_global || attribute.usage_count > 0;
                });

                const documentTypesWithAttribute = new Set(
                    schemasWithAttribute.map((s) => s.document_type)
                );
                filtered = filtered.filter((dt) => documentTypesWithAttribute.has(dt.key));
            }
        }

        setDocumentTypesWithStats(filtered);
        setIsLoading(false);
    };

    const handleOpenModal = (docType?: DocumentTypeWithStats) => {
        if (docType) {
            setEditingDocType(docType);
            setFormData({
                key: docType.key,
                label: docType.label,
                description: docType.description,
            });
        } else {
            setEditingDocType(null);
            setFormData({
                key: '' as DocumentType | '',
                label: '',
                description: '',
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingDocType(null);
    };

    const handleSave = () => {
        // In real implementation, this would call an API
        // For now, we'll just show a message
        if (editingDocType) {
            // Update existing
            alert(t('types.modal.updateSuccess'));
        } else {
            // Create new - in real implementation, this would add to DOCUMENT_TYPES
            // For now, we can't actually add new document types as they're from enum
            alert(t('types.modal.createSuccess'));
        }
        handleCloseModal();
        loadDocumentTypes();
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-primary">{t('types.title')}</h2>
                    <p className="text-tertiary mt-1">{t('types.description')}</p>
                </div>
                <Button
                    onClick={() => handleOpenModal()}
                    size="md"
                    color="primary"
                    iconLeading={Plus}
                >
                    {t('types.newDocumentType')}
                </Button>
            </div>

            {/* Filters */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Input
                        placeholder={t('types.filters.keywordPlaceholder')}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        icon={SearchLg}
                        size="md"
                    />

                    <Select
                        placeholder={t('types.filters.attributePlaceholder')}
                        items={attributeOptions}
                        selectedKey={attributeFilter}
                        onSelectionChange={(key) => setAttributeFilter(key ? String(key) : '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            {/* Document Types Table */}
            <TableCard.Root>
                <TableCard.Header
                    title={`${t('types.title')} (${documentTypesWithStats.length})`}
                    description={t('types.description')}
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('types.headers.key')} isRowHeader />
                        <Table.Head label={t('types.headers.label')} />
                        <Table.Head label={t('types.headers.description')} />
                        <Table.Head label={t('types.headers.schemasCount')} />
                        <Table.Head label={t('types.headers.attributesCount')} />
                        <Table.Head label={t('types.headers.actions')} />
                    </Table.Header>
                    <Table.Body>
                        {documentTypesWithStats.map((docType) => (
                            <Table.Row key={docType.key}>
                                <Table.Cell>
                                    <div className="font-medium text-primary font-mono">{docType.key}</div>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-primary">{docType.label}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">{docType.description}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-primary">{docType.schemasCount}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-primary">{docType.attributesCount}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="flex items-center gap-2">
                                        <Link
                                            to={`/admin/documents/types/${docType.key}/schemas`}
                                            className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                                        >
                                            {t('types.actions.viewSchemas')}
                                            <ArrowRight className="size-4" />
                                        </Link>
                                        <Link
                                            to={`/admin/documents/types/${docType.key}/attributes`}
                                            className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                                        >
                                            {t('types.actions.viewAttributes')}
                                            <FileCheck02 className="size-4" />
                                        </Link>
                                        <Button
                                            onClick={() => handleOpenModal(docType)}
                                            size="sm"
                                            color="secondary"
                                            iconLeading={Edit01}
                                        >
                                            {t('types.actions.edit')}
                                        </Button>
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {documentTypesWithStats.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-tertiary mb-4">
                        <h3 className="text-lg font-medium text-primary mb-2">{t('noData.title')}</h3>
                        <p className="text-tertiary">{t('noData.message')}</p>
                    </div>
                </div>
            )}

            {/* Modal */}
            <DialogTrigger isOpen={isModalOpen} onOpenChange={setIsModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-4xl rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-6">
                                    {editingDocType ? t('types.modal.edit') : t('types.modal.new')}
                                </h2>

                                <div className="space-y-4">
                                    <Input
                                        label={t('types.modal.fields.key')}
                                        placeholder={t('types.modal.fields.keyPlaceholder')}
                                        value={formData.key}
                                        onChange={(value) => setFormData({ ...formData, key: value as DocumentType })}
                                        isRequired
                                        size="md"
                                        isDisabled={!!editingDocType}
                                    />

                                    <Input
                                        label={t('types.modal.fields.label')}
                                        placeholder={t('types.modal.fields.labelPlaceholder')}
                                        value={formData.label}
                                        onChange={(value) => setFormData({ ...formData, label: value })}
                                        isRequired
                                        size="md"
                                    />

                                    <Input
                                        label={t('types.modal.fields.description')}
                                        placeholder={t('types.modal.fields.descriptionPlaceholder')}
                                        value={formData.description}
                                        onChange={(value) => setFormData({ ...formData, description: value })}
                                        size="md"
                                    />
                                </div>

                                <div className="flex items-center justify-end gap-4 mt-6">
                                    <Button onClick={handleCloseModal} size="md" color="secondary">
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button onClick={handleSave} size="md" color="primary">
                                        {editingDocType ? t('modal.save') : t('modal.create')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>
        </div>
    );
};
